'use client';
import { useState, useEffect, FC } from 'react';
import { Variant } from 'shared/types/product';
import Head from 'next/head';
import { Product } from 'shared/types/product/Product';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import usePath from 'helpers/hooks/usePath';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { toUIColor } from 'helpers/mappers/toUIColor';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUISize } from 'helpers/mappers/toUIsize';
import ProductDetails, { ProductDetailsProps } from '..';
import { VARAINT_CHANGE_EVENT } from 'helpers/constants/events';
import { COLOR } from 'helpers/constants/attributes';
import { useParams } from 'next/navigation';
import { getPdpUrlWithLocale, getSkuFromUrlOrSlug } from 'helpers/utils/pdpUrlHelper';

type ProductDetailsAdapterProps = {
  product: Product;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({ product, inModalVersion, setIsOpen, onAddToCart }) => {
  const { path } = usePath();

  const getCurrentVariant = () => {
    const currentVariantSKU = getSkuFromUrlOrSlug(path);
    const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);
    return product.variants[currentVariantIndex] ?? product.variants?.[0];
  }
  const getMappedProduct = () => {
    const colors = toUIColor(product);
    const sizes = toUISize(product);
    const productToUse = toUIProduct(product, variant, colors, sizes);  
    return { ...productToUse, images: [productToUse.images[0]] };
  }

  const [variant, setVariant] = useState<Variant>(getCurrentVariant());
  const [mappedProduct, setMappedProduct] = useState<UIProduct>(getMappedProduct());

  usePreloadImages(product.variants.map((variant) => variant.images ?? []).flat());

  useEffect(() => {
    if (product && variant) {
      setMappedProduct(getMappedProduct());
    }
  }, [product, variant]);

  useEffect(() => {
    if (!product) return;

    if (inModalVersion) {
      setVariant(product?.variants[0]);
    } else {
      setVariant(getCurrentVariant());
    }
  }, [inModalVersion, product, path]);
  const { locale } = useParams();
  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);
    setVariant(variantsToUse ?? product.variants?.[0]);
    if(product.slug){
      const newUrl = getPdpUrlWithLocale(locale, product.slug, variantsToUse?.sku, variantsToUse?.attributes?.colour);
      window.history.pushState({}, '', newUrl);
    }
  };

  if (!product || !variant) return <></>;

  return (
    <>
    <ProductDetails
      product={mappedProduct as UIProduct}
      variant={variant}
      url={product._url}
      inModalVersion={inModalVersion}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
    />
    </>
  );
};

export default ProductDetailsAdapter;
